import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import Gauge from "@/components/dashboard/GaugeChart";
import KpiCard from "@/components/dashboard/KpiCard";
import SubsystemMatrix, { StatusPill, SubsystemNameCell, SUBSYSTEM_COLORS } from "@/components/dashboard/SubsystemMatrix";
import { CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, AreaChart, Area, Legend } from "recharts";

function Panel({ n, title, children, className = "" }) {
  return (
    <div className={`bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow ${className}`}>
      <div className="flex items-center mb-4">
        <span className="text-[#93c5fd] mr-2 font-black text-lg">{n}.</span>
        <span className="text-[#1e40af] font-bold text-sm uppercase tracking-tight">{title}</span>
      </div>
      {children}
    </div>
  );
}

export default function Availability() {
  const { params } = useFilters();
  const [summary, setSummary] = useState(null);
  const [pareto, setPareto] = useState({ items: [] });
  const [matrix, setMatrix] = useState({ items: [], targets: {} });
  const [trend, setTrend] = useState({ items: [] });

  useEffect(() => {
    (async () => {
      const [s, p, t, mx] = await Promise.all([
        api.get("/metrics/summary", { params }),
        api.get("/metrics/pareto", { params }),
        api.get("/metrics/trend", { params }),
        api.get("/metrics/subsystem_matrix", { params }),
      ]);
      setSummary(s.data); setPareto(p.data); setTrend(t.data); setMatrix(mx.data);
    })();
  }, [params.start, params.end, params.line, params.subsystem]);

  const c = summary?.current || {};
  const prev = summary?.previous;
  const sett = summary?.settings || {};

  const opHoursTotal = c.op_hours_total || 0;
  const downtimeHours = (c.total_delay_minutes || 0) / 60;
  const uptime = opHoursTotal - downtimeHours;
  const target = sett.target_availability || 98.5;

  const avail = c.availability_percent || 0;
  const availTrend = trend.items.map(i => {
    const opH = (trend.granularity === "monthly" ? 30 : trend.granularity === "weekly" ? 7 : 1) * (sett.op_hours_per_day || 18);
    const dt = (i.downtime_min || 0) / 60;
    return { key: i.key, "Availability": opH > 0 ? Number(((opH - dt) / opH * 100).toFixed(2)) : 100 };
  });

  const fmt = (k) => {
    const d = new Date(k);
    if (trend.granularity === "monthly") return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div className="space-y-6 pb-8" data-testid="detail-availability">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1e40af] to-[#3b82f6] text-white p-8 rounded-2xl relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-white/5" />
        <div className="absolute -right-8 -bottom-16 w-48 h-48 rounded-full bg-white/5" />
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Current Availability</div>
            <div className="text-6xl font-black tracking-tight mt-2 tabular-nums">{avail}<span className="text-3xl">%</span></div>
            <div className="text-xs text-white/80 mt-2">Target ≥ {target}% · {avail >= target ? "✓ On Target" : "⚠ Below Target"}</div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Uptime</div>
            <div className="text-3xl font-black mt-1 tabular-nums">{uptime.toFixed(1)}<span className="text-sm ml-1 text-white/70">Hours</span></div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Downtime</div>
            <div className="text-3xl font-black mt-1 tabular-nums">{downtimeHours.toFixed(2)}<span className="text-sm ml-1 text-white/70">Hours</span></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard testId="kpi-avail" label="Availability (%)" value={c.availability_percent} unit="%" prev={prev?.availability_percent} direction="up_good" icon={CheckCircle2} iconColor="#22c55e" iconBg="#dcfce7" prevLabel="prev period" />
        <KpiCard testId="kpi-avail-events" label="Total Failure Events" value={c.total_failure_events} prev={prev?.total_failure_events} direction="down_good" icon={AlertTriangle} iconColor="#f59e0b" iconBg="#fef3c7" prevLabel="prev period" />
        <KpiCard testId="kpi-avail-delay" label="Total Delay (Min)" value={c.total_delay_minutes} prev={prev?.total_delay_minutes} direction="down_good" icon={Clock} iconColor="#3b82f6" iconBg="#dbeafe" prevLabel="prev period" />
      </div>

      <Panel n="1" title="Availability Trend vs Target">
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={availTrend.map(t => ({ ...t, key: fmt(t.key) }))} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="availGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22c55e" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="key" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
            <YAxis domain={[Math.max(0, Math.min(...availTrend.map(t => t.Availability), target) - 1), 100]} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} unit="%" />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <ReferenceLine y={target} stroke="#f59e0b" strokeDasharray="5 3" label={{ value: `Target ${target}%`, fill: "#f59e0b", fontSize: 10, position: "right" }} />
            <Area type="monotone" dataKey="Availability" stroke="#22c55e" strokeWidth={2.5} fill="url(#availGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </Panel>

      <Panel n="2" title="Downtime Contribution by Subsystem">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={pareto.items} layout="vertical" margin={{ top: 10, right: 30, left: 30, bottom: 0 }}>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} unit=" min" />
            <YAxis type="category" dataKey="subsystem" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={80} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} cursor={{ fill: "#f1f5f9" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
            <Bar dataKey="downtime_min" name="Downtime (Minutes)" fill="#ef4444" radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Panel>

      <SubsystemMatrix
        testId="availability-subsystem-matrix"
        panelIndex="3"
        title="Matriks Availability per Subsistem"
        subtitle="Availability = (Op-Hours − Downtime Subsistem) ÷ Op-Hours × 100%"
        target={{ label: `Target ≥ ${matrix.targets?.availability_percent ?? target}%` }}
        items={matrix.items}
        columns={[
          { key: "subsystem", label: "Subsistem", render: (v) => <SubsystemNameCell name={v} colorMap={SUBSYSTEM_COLORS} /> },
          { key: "events", label: "Total Events", align: "right" },
          { key: "downtime_min", label: "Downtime (menit)", align: "right", render: (v) => v.toFixed(2), tone: () => "text-rose-700" },
          { key: "downtime_hours", label: "Downtime (jam)", align: "right", render: (v) => v.toFixed(2) },
          { key: "availability_percent", label: "Availability %", align: "right", render: (v) => `${v.toFixed(2)}%`, tone: (v) => (v >= (matrix.targets?.availability_percent ?? target) ? "text-emerald-700 font-bold" : "text-rose-700 font-bold") },
          { key: "_status", label: "Status", align: "right",
            render: (_, row) => {
              const goal = matrix.targets?.availability_percent ?? target;
              return <StatusPill ok={row.availability_percent >= goal} />;
            } },
        ]}
      />
    </div>
  );
}
